import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useAuth } from "../../../hooks/useAuth";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "../../ui/card";
import { useState, useEffect } from "react";
import { ThemeToggle } from "../../ui/theme-toggle";
import { useQuery } from "@tanstack/react-query";
import { departmentsService } from "../../../services/departments.service";
import { ceToBe } from "../../../utils/dateUtils";
import { DatePicker } from "../../ui/date-picker";

const registerSchema = z
  .object({
    email: z.string().email("กรุณากรอกอีเมลที่ถูกต้อง"),
    password: z.string().min(8, "รหัสผ่านต้องมีอย่างน้อย 8 ตัวอักษร"),
    confirmPassword: z.string().min(8, "กรุณายืนยันรหัสผ่าน"),
    firstName: z.string().min(1, "กรุณากรอกชื่อ"),
    lastName: z.string().min(1, "กรุณากรอกนามสกุล"),
    employeeId: z.string().optional(), // Optional, auto-generated
    departmentId: z.string().min(1, "กรุณาเลือกแผนก"),
    teamId: z.string().optional(),
    position: z.string().optional(),
    phoneNumber: z.string().optional(),
    hireDate: z.string().optional(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "รหัสผ่านไม่ตรงกัน",
    path: ["confirmPassword"],
  });

type RegisterFormData = z.infer<typeof registerSchema>;

export function RegisterForm() {
  const { registerAsync, isRegisterLoading } = useAuth();
  const [error, setError] = useState<string>("");
  const [success, setSuccess] = useState<boolean>(false);

  // Fetch departments from API
  const {
    data: departmentsData,
    isLoading: isDepartmentsLoading,
    isError: isDepartmentsError,
  } = useQuery({
    queryKey: ["departments"],
    queryFn: () => departmentsService.getDepartments(),
    retry: 1,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  const departments = departmentsData?.departments || [];

  // Generate employee ID on component mount
  const { data: employeeIdData } = useQuery({
    queryKey: ["generate-employee-id"],
    queryFn: async () => {
      const authService = (await import("../../../services/auth.service"))
        .default;
      return authService.generateEmployeeId();
    },
    retry: 2,
    staleTime: Infinity, // Don't refetch
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
  });

  // Watch hireDate value from form
  const hireDate = watch("hireDate");

  // Update form when employee ID is generated
  useEffect(() => {
    if (employeeIdData) {
      setValue("employeeId", employeeIdData);
    }
  }, [employeeIdData, setValue]);

  const onSubmit = async (data: RegisterFormData) => {
    try {
      setError("");
      setSuccess(false);

      // Remove confirmPassword before sending
      const { confirmPassword, ...registerData } = data;

      await registerAsync(registerData);
      setSuccess(true);

      // Redirect to login after successful registration (since account is inactive)
      setTimeout(() => {
        window.location.href = "/login";
      }, 3000);
    } catch (err: any) {
      setError(
        err.response?.data?.error?.message ||
          "สมัครสมาชิกไม่สำเร็จ กรุณาลองใหม่อีกครั้ง"
      );
    }
  };

  return (
    <Card className="w-full max-w-2xl">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>สมัครสมาชิก</CardTitle>
          <ThemeToggle />
        </div>
        <CardDescription>
          สร้างบัญชีผู้ใช้งานใหม่สำหรับระบบจัดการการลางาน
        </CardDescription>
      </CardHeader>
      <form onSubmit={handleSubmit(onSubmit)}>
        <CardContent className="space-y-4">
          {error && (
            <div className="p-3 text-sm text-red-500 bg-red-50 border border-red-200 rounded-md">
              {error}
            </div>
          )}

          {success && (
            <div className="p-4 text-sm bg-blue-50 border border-blue-200 rounded-md">
              <div className="flex items-start gap-3">
                <svg
                  className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                    clipRule="evenodd"
                  />
                </svg>
                <div className="flex-1">
                  <p className="font-semibold text-blue-900 mb-1">
                    ✅ สมัครสมาชิกสำเร็จ!
                  </p>
                  <p className="text-blue-800 mb-2">
                    บัญชีของคุณอยู่ระหว่างรอการอนุมัติจากผู้ดูแลระบบ
                  </p>
                  <p className="text-blue-700 text-xs">
                    คุณจะได้รับการแจ้งเตือนเมื่อบัญชีของคุณได้รับการอนุมัติแล้ว
                    กำลังนำคุณกลับไปหน้าเข้าสู่ระบบ...
                  </p>
                </div>
              </div>
            </div>
          )}

          {isDepartmentsError && (
            <div className="p-3 text-sm text-red-500 bg-red-50 border border-red-200 rounded-md">
              ไม่สามารถโหลดข้อมูลแผนกได้ กรุณาลองใหม่อีกครั้ง
            </div>
          )}

          {/* Personal Information */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-gray-700">
              ข้อมูลส่วนตัว
            </h3>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label
                  htmlFor="firstName"
                  className="text-sm font-medium leading-none"
                >
                  ชื่อ <span className="text-red-500">*</span>
                </label>
                <Input
                  id="firstName"
                  type="text"
                  placeholder="ชื่อ"
                  {...register("firstName")}
                  disabled={isRegisterLoading}
                />
                {errors.firstName && (
                  <p className="text-sm text-red-500">
                    {errors.firstName.message}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <label
                  htmlFor="lastName"
                  className="text-sm font-medium leading-none"
                >
                  นามสกุล <span className="text-red-500">*</span>
                </label>
                <Input
                  id="lastName"
                  type="text"
                  placeholder="นามสกุล"
                  {...register("lastName")}
                  disabled={isRegisterLoading}
                />
                {errors.lastName && (
                  <p className="text-sm text-red-500">
                    {errors.lastName.message}
                  </p>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <label
                htmlFor="email"
                className="text-sm font-medium leading-none"
              >
                อีเมล <span className="text-red-500">*</span>
              </label>
              <Input
                id="email"
                type="email"
                placeholder="your.email@company.com"
                {...register("email")}
                disabled={isRegisterLoading}
              />
              {errors.email && (
                <p className="text-sm text-red-500">{errors.email.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <label
                htmlFor="phoneNumber"
                className="text-sm font-medium leading-none"
              >
                เบอร์โทรศัพท์
              </label>
              <Input
                id="phoneNumber"
                type="tel"
                placeholder="08XXXXXXXX"
                {...register("phoneNumber")}
                disabled={isRegisterLoading}
              />
              {errors.phoneNumber && (
                <p className="text-sm text-red-500">
                  {errors.phoneNumber.message}
                </p>
              )}
            </div>
          </div>

          {/* Employment Information */}
          <div className="space-y-4 pt-4 border-t">
            <h3 className="text-sm font-semibold text-gray-700">
              ข้อมูลการทำงาน
            </h3>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label
                  htmlFor="employeeId"
                  className="text-sm font-medium leading-none"
                >
                  รหัสพนักงาน{" "}
                  <span className="text-gray-400">(สร้างอัตโนมัติ)</span>
                </label>
                <div className="relative">
                  <Input
                    id="employeeId"
                    type="text"
                    placeholder={
                      employeeIdData
                        ? employeeIdData
                        : "กำลังสร้างรหัสพนักงาน..."
                    }
                    {...register("employeeId")}
                    readOnly
                    className="bg-gray-50 cursor-not-allowed"
                  />
                  {employeeIdData && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      <svg
                        className="w-5 h-5 text-green-500"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                  )}
                </div>
                {employeeIdData && (
                  <p className="text-xs text-green-600">
                    ✓ รหัสพนักงานถูกสร้างเรียบร้อยแล้ว
                  </p>
                )}
                {errors.employeeId && (
                  <p className="text-sm text-red-500">
                    {errors.employeeId.message}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <label
                  htmlFor="position"
                  className="text-sm font-medium leading-none"
                >
                  ตำแหน่ง
                </label>
                <Input
                  id="position"
                  type="text"
                  placeholder="Software Engineer"
                  {...register("position")}
                  disabled={isRegisterLoading}
                />
                {errors.position && (
                  <p className="text-sm text-red-500">
                    {errors.position.message}
                  </p>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <label
                htmlFor="departmentId"
                className="text-sm font-medium leading-none"
              >
                แผนก <span className="text-red-500">*</span>
              </label>
              <select
                id="departmentId"
                {...register("departmentId")}
                disabled={
                  isRegisterLoading ||
                  isDepartmentsLoading ||
                  isDepartmentsError
                }
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <option value="">
                  {isDepartmentsLoading
                    ? "กำลังโหลด..."
                    : isDepartmentsError
                      ? "ไม่สามารถโหลดแผนกได้"
                      : "-- เลือกแผนก --"}
                </option>
                {!isDepartmentsError &&
                  departments.map((dept) => (
                    <option key={dept.id} value={dept.id}>
                      {dept.name}
                    </option>
                  ))}
              </select>
              {errors.departmentId && (
                <p className="text-sm text-red-500">
                  {errors.departmentId.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <label
                htmlFor="hireDate"
                className="text-sm font-medium leading-none"
              >
                วันที่เริ่มงาน
              </label>
              <DatePicker
                value={hireDate || ""}
                onChange={(ceDate) =>
                  setValue("hireDate", ceDate || "", {
                    shouldDirty: true,
                    shouldValidate: true,
                  })
                }
                disabled={isRegisterLoading}
                placeholder="เลือกวันที่"
                className="w-full"
              />
              {hireDate && (
                <p className="text-xs text-gray-500 mt-1">
                  พ.ศ. {ceToBe(new Date(hireDate).getFullYear())}
                </p>
              )}
              {errors.hireDate && (
                <p className="text-sm text-red-500">
                  {errors.hireDate.message}
                </p>
              )}
            </div>
          </div>

          {/* Security */}
          <div className="space-y-4 pt-4 border-t">
            <h3 className="text-sm font-semibold text-gray-700">ความปลอดภัย</h3>

            <div className="space-y-2">
              <label
                htmlFor="password"
                className="text-sm font-medium leading-none"
              >
                รหัสผ่าน <span className="text-red-500">*</span>
              </label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                {...register("password")}
                disabled={isRegisterLoading}
              />
              {errors.password && (
                <p className="text-sm text-red-500">
                  {errors.password.message}
                </p>
              )}
              <p className="text-xs text-gray-500">
                รหัสผ่านต้องมีอย่างน้อย 8 ตัวอักษร
              </p>
            </div>

            <div className="space-y-2">
              <label
                htmlFor="confirmPassword"
                className="text-sm font-medium leading-none"
              >
                ยืนยันรหัสผ่าน <span className="text-red-500">*</span>
              </label>
              <Input
                id="confirmPassword"
                type="password"
                placeholder="••••••••"
                {...register("confirmPassword")}
                disabled={isRegisterLoading}
              />
              {errors.confirmPassword && (
                <p className="text-sm text-red-500">
                  {errors.confirmPassword.message}
                </p>
              )}
            </div>
          </div>
        </CardContent>

        <CardFooter className="flex flex-col space-y-4">
          <Button
            type="submit"
            className="w-full"
            disabled={isRegisterLoading || success}
          >
            {isRegisterLoading
              ? "กำลังสมัครสมาชิก..."
              : success
                ? "สมัครสมาชิกสำเร็จ!"
                : "สมัครสมาชิก"}
          </Button>

          <p className="text-sm text-center text-gray-600">
            มีบัญชีอยู่แล้ว?{" "}
            <a href="/login" className="text-primary hover:underline">
              เข้าสู่ระบบ
            </a>
          </p>
        </CardFooter>
      </form>
    </Card>
  );
}
